const STORAGE_KEY = 'dariiaComingSoonAttribution';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'];

function readStoredAttribution() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
  } catch (error) {
    return {};
  }
}

function writeStoredAttribution(data) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    // Ignore private-mode storage errors.
  }
}

function hostnameFromUrl(value) {
  try {
    return new URL(value).hostname.replace(/^www\./, '');
  } catch (error) {
    return '';
  }
}

function captureAttribution() {
  const params = new URLSearchParams(window.location.search);
  const next = readStoredAttribution();

  UTM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) next[key] = value;
  });

  if (params.get('fbclid')) {
    next.fbclid = '1';
    if (!next.utm_source) next.utm_source = 'facebook';
    if (!next.utm_medium) next.utm_medium = 'paid';
  }

  if (params.get('gclid') || params.get('gbraid') || params.get('wbraid')) {
    if (!next.utm_source) next.utm_source = 'google';
    if (!next.utm_medium) next.utm_medium = 'cpc';
  }

  if (params.get('ttclid') && !next.utm_source) {
    next.utm_source = 'tiktok';
    next.utm_medium = next.utm_medium || 'paid';
  }

  if (document.referrer) {
    const referrerHost = hostnameFromUrl(document.referrer);
    if (referrerHost && referrerHost !== window.location.hostname && !next.referrer) {
      next.referrer = document.referrer;
    }
  }

  if (!next.landing) {
    next.landing = `${window.location.pathname}${window.location.search}`;
  }

  writeStoredAttribution(next);
  return next;
}

function resolveSource(data) {
  const source = (data.utm_source || '').toLowerCase();
  const medium = (data.utm_medium || '').toLowerCase();
  const referrer = (data.referrer || '').toLowerCase();
  const paid = /cpc|ppc|paid|ads|paidsocial|paid_social/.test(medium);

  if (source.includes('instagram') || source === 'ig' || referrer.includes('instagram.com')) {
    return paid || source.includes('ad') ? 'Instagram ads' : 'Instagram';
  }

  if (
    source.includes('facebook') ||
    source.includes('meta') ||
    source === 'fb' ||
    data.fbclid ||
    referrer.includes('facebook.com') ||
    referrer.includes('fb.com') ||
    referrer.includes('l.facebook') ||
    referrer.includes('lm.facebook')
  ) {
    return paid || source.includes('meta') || data.fbclid ? 'Meta ads' : 'Facebook';
  }

  if (source.includes('tiktok') || referrer.includes('tiktok.com')) return paid ? 'TikTok ads' : 'TikTok';
  if (source.includes('google') || referrer.includes('google.')) return paid ? 'Google ads' : 'Google';
  if (source.includes('pinterest') || referrer.includes('pinterest.')) return 'Pinterest';
  if (source.includes('youtube') || referrer.includes('youtube.com')) return 'YouTube';
  if (source) return data.utm_source;
  if (data.referrer) return hostnameFromUrl(data.referrer) || 'referral';
  return 'direct';
}

function sanitizeTagPart(value) {
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/,/g, ' ')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9:_-]/g, '')
    .slice(0, 80);
}

function buildTags(baseTag, data, source) {
  const tags = [];
  const add = (value) => {
    const next = sanitizeTagPart(value);
    if (next && !tags.includes(next)) tags.push(next);
  };

  String(baseTag || '')
    .split(',')
    .forEach((tag) => add(tag));

  add(`source:${source}`);

  UTM_KEYS.forEach((key) => {
    if (data[key]) add(`${key}:${data[key]}`);
  });

  return tags.join(', ');
}

function buildNote(data, source) {
  const lines = [`Source: ${source}`];

  UTM_KEYS.forEach((key) => {
    if (data[key]) lines.push(`${key}: ${data[key]}`);
  });

  if (data.referrer) lines.push(`Referrer: ${data.referrer}`);
  if (data.landing) lines.push(`Landing: ${data.landing}`);

  return lines.join('\n');
}

class ComingSoonForm extends HTMLElement {
  connectedCallback() {
    this.formPanel = this.querySelector('[data-coming-soon-form-panel]');
    this.form = this.querySelector('.coming-soon__form');
    this.tagsInput = this.querySelector('[data-customer-tags]');
    this.noteInput = this.querySelector('[data-customer-note]');
    this.baseTag = this.tagsInput?.dataset.baseTag || '';
    this.isPopup = this.hasAttribute('data-popup');
    this.storageKey = this.dataset.storageKey || 'comingSoonPopup';
    this.delay = Number.parseInt(this.dataset.delay || '0', 10) * 1000;
    this.section = this.closest('.shopify-section');

    this.attribution = captureAttribution();
    this.applyAttribution();

    this.onSubmit = this.onSubmit.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onCloseClick = this.onCloseClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onSectionSelect = this.onSectionSelect.bind(this);
    this.onSectionDeselect = this.onSectionDeselect.bind(this);

    this.form?.addEventListener('submit', this.onSubmit);
    window.addEventListener('resize', this.onResize);
    this.closeButtons = Array.from(this.querySelectorAll('[data-coming-soon-close]'));
    this.closeButtons.forEach((el) => {
      el.addEventListener('click', this.onCloseClick);
    });

    if (this.formPanel && 'ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.onResize());
      this.resizeObserver.observe(this.formPanel);
    }

    this.onResize();
    this.setupPopup();
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmit);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('keydown', this.onKeyDown);
    this.closeButtons?.forEach((el) => {
      el.removeEventListener('click', this.onCloseClick);
    });
    this.resizeObserver?.disconnect();
    this.section?.removeEventListener('shopify:section:select', this.onSectionSelect);
    this.section?.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
    window.clearTimeout(this.openTimer);
  }

  get isDesignMode() {
    return Boolean(window.Shopify?.designMode);
  }

  setupPopup() {
    if (!this.isPopup) return;

    this.section?.addEventListener('shopify:section:select', this.onSectionSelect);
    this.section?.addEventListener('shopify:section:deselect', this.onSectionDeselect);
    document.addEventListener('keydown', this.onKeyDown);

    if (this.isDesignMode) {
      this.openPopup();
      return;
    }

    if (this.hasAttribute('data-posted')) {
      this.persistDismissed();
      this.openPopup();
      return;
    }

    if (this.isDismissed()) return;

    this.openTimer = window.setTimeout(() => this.openPopup(), this.delay);
  }

  isDismissed() {
    try {
      return localStorage.getItem(this.storageKey) === 'true';
    } catch (error) {
      return false;
    }
  }

  persistDismissed() {
    if (this.isDesignMode) return;
    try {
      localStorage.setItem(this.storageKey, 'true');
    } catch (error) {
      // Ignore private-mode storage errors.
    }
  }

  openPopup() {
    this.classList.add('is-open');
    document.body.classList.add('no-scroll');
    this.onResize();
  }

  closePopup() {
    this.classList.remove('is-open');
    document.body.classList.remove('no-scroll');
  }

  dismissPopup() {
    this.persistDismissed();
    this.closePopup();
  }

  onCloseClick(event) {
    event.preventDefault();
    this.dismissPopup();
  }

  onKeyDown(event) {
    if (event.key === 'Escape' && this.classList.contains('is-open')) {
      this.dismissPopup();
    }
  }

  onSectionSelect() {
    window.clearTimeout(this.openTimer);
    this.openPopup();
  }

  onSectionDeselect() {
    this.closePopup();
  }

  applyAttribution() {
    const source = resolveSource(this.attribution);
    if (this.tagsInput) this.tagsInput.value = buildTags(this.baseTag, this.attribution, source);
    if (this.noteInput) this.noteInput.value = buildNote(this.attribution, source);
  }

  onResize() {
    if (!this.formPanel) return;

    if (this.isPopup) {
      this.style.setProperty('--coming-soon-form-offset', '0px');
      return;
    }

    const mobile = window.matchMedia('(max-width: 999px)').matches;
    const offset = mobile ? `${this.formPanel.offsetHeight}px` : '0px';
    this.style.setProperty('--coming-soon-form-offset', offset);
  }

  onSubmit() {
    this.attribution = captureAttribution();
    this.applyAttribution();
    if (this.isPopup) this.persistDismissed();
  }
}

if (!customElements.get('coming-soon-form')) {
  customElements.define('coming-soon-form', ComingSoonForm);
}
